import type { ClienteApi, ConVersion } from '../cliente';
import { claveIdempotencia, consulta, negocio, type Pagina } from '../comun';
import { ErrorApi } from '../problema';
import type { components, operations } from '../tipos/canales';

/**
 * Operaciones de «Canales» (11.1 Canales, 11.2 Conectar un canal y 11.4 Plantillas de Meta) con su `operationId` y los
 * tipos generados de `canales.yaml`. Ver los canales, su salud y sus incidencias pide `canales.salud.ver`; conectar,
 * cambiar y desconectar, `canales.canales.gestionar`; las plantillas de Meta, `canales.plantillas.gestionar`.
 *
 * - Un canal se cambia con `If-Match` (su `ETag` de getChannel).
 * - Empezar y completar un alta y enviar una plantilla llevan `Idempotency-Key` (obligatoria); cancelar un alta,
 *   desconectar, comprobar la salud y retirar una plantilla, también (opcional en el contrato).
 */
type Esquema<N extends keyof components['schemas']> = components['schemas'][N];

export type Canal = Esquema<'Channel'>;
export type TipoCanal = Esquema<'ChannelType'>;
export type EstadoCanal = Esquema<'ChannelStatus'>;
export type CambioCanal = Esquema<'ChannelUpdate'>;
export type Incidencia = Esquema<'Incident'>;
export type TipoIncidencia = Esquema<'IncidentKind'>;
export type ListaIncidencias = Esquema<'IncidentList'>;
export type Comprobacion = Esquema<'HealthCheck'>;
export type Alta = Esquema<'Connection'>;
export type InicioAlta = Esquema<'ConnectionStart'>;
export type DatosAlta = Esquema<'ConnectionComplete'>;
export type PlantillaMeta = Esquema<'MetaTemplate'>;
export type ListaPlantillasMeta = Esquema<'MetaTemplateList'>;
export type NuevaPlantillaMeta = Esquema<'MetaTemplateCreate'>;
export type TipoVariable = Esquema<'TemplateVariableKind'>;
export type ParteDePlantilla = Esquema<'TemplateComponent'>;
export type EstadoPlantilla = Esquema<'TemplateStatus'>;
export type CategoriaMeta = Esquema<'MetaCategory'>;
export type ConflictoAlta = Esquema<'ConnectionConflictProblem'>;
type ConsultaCanales = NonNullable<operations['listChannels']['parameters']['query']>;
type ConsultaPlantillas = NonNullable<operations['listMetaTemplates']['parameters']['query']>;

/**
 * El alta abierta del mismo tipo que trae el `409 conflict` de `startChannelConnection` (`ConnectionConflictProblem`,
 * G687), para «Seguir con la que empecé» o «Cancelarla». null si el error es otro o no la trae.
 */
export function altaAbiertaDelConflicto(e: unknown): string | null {
    if (!(e instanceof ErrorApi) || e.codigo !== 'conflict') {
        return null;
    }
    const id = (e.problema as ConflictoAlta).connection_id;
    return typeof id === 'string' && id !== '' ? id : null;
}

const r = (businessId: string, resto: string): `/v1/${string}` => `${negocio(businessId)}${resto}`;
const id = encodeURIComponent;

export function operacionesCanales(api: ClienteApi) {
    return {
        // ---- 11.1 Canales ----
        /** listChannels (`canales.salud.ver`): conectados, en alta y desconectados; nunca credenciales. */
        listChannels: (businessId: string, filtros: ConsultaCanales = {}) => api.pedir<Esquema<'ChannelList'>>('GET', r(businessId, `/channels${consulta({ ...filtros })}`)),
        /** getChannel: el canal con su salud y su `ETag` (para cambiarlo). */
        getChannel: (businessId: string, canalId: string): Promise<ConVersion<Canal>> => api.pedirConVersion<Canal>('GET', r(businessId, `/channels/${id(canalId)}`)),
        /** updateChannel (`canales.canales.gestionar`): nombre, local o por defecto, con la versión leída. */
        updateChannel: (businessId: string, canalId: string, cambio: CambioCanal, version: string): Promise<ConVersion<Canal>> =>
            api.pedirConVersion<Canal>('PATCH', r(businessId, `/channels/${id(canalId)}`), { cuerpo: cambio, siCoincide: version }),
        /** disconnectChannel: con motivo (queda en Actividad); también en solo lectura. */
        disconnectChannel: (businessId: string, canalId: string, motivo: string, clave = claveIdempotencia()) =>
            api.pedir<Canal>('POST', r(businessId, `/channels/${id(canalId)}/disconnect`), { cuerpo: { reason: motivo }, idempotencia: clave }),
        /** runChannelHealthCheck (`canales.salud.ver`): la misma comprobación que cada 5 minutos. */
        runChannelHealthCheck: (businessId: string, canalId: string, clave = claveIdempotencia()) =>
            api.pedir<Comprobacion>('POST', r(businessId, `/channels/${id(canalId)}/health-checks`), { idempotencia: clave }),
        /** listChannelIncidents: de la más reciente a la más antigua; `open` = solo las abiertas o solo las resueltas. */
        listChannelIncidents: (businessId: string, canalId: string, filtros: { open?: boolean } & Pagina = {}) =>
            api.pedir<ListaIncidencias>('GET', r(businessId, `/channels/${id(canalId)}/incidents${consulta({ ...filtros })}`)),

        // ---- 11.2 Conectar un canal ----
        /** startChannelConnection: el bloque de cada tipo lo comprueba la API (403 `entitlement_required`); 409 con el límite de cuentas de WhatsApp o con otra alta abierta del mismo tipo (con su `connection_id`, G687); 422 con `web_chat` (G685). */
        startChannelConnection: (businessId: string, inicio: InicioAlta, clave = claveIdempotencia()) =>
            api.pedir<Alta>('POST', r(businessId, '/channel-connections'), { cuerpo: inicio, idempotencia: clave }),
        /** getChannelConnection: cómo va el alta (registros DNS, motivo del fallo). */
        getChannelConnection: (businessId: string, altaId: string) => api.pedir<Alta>('GET', r(businessId, `/channel-connections/${id(altaId)}`)),
        /** completeChannelConnection: el código y el `state` de la ventana de Meta, el token del bot o el dominio. */
        completeChannelConnection: (businessId: string, altaId: string, datos: DatosAlta, clave = claveIdempotencia()) =>
            api.pedir<Alta>('POST', r(businessId, `/channel-connections/${id(altaId)}/complete`), { cuerpo: datos, idempotencia: clave }),
        /** cancelChannelConnection: abandona un alta a medias (también en solo lectura). */
        cancelChannelConnection: (businessId: string, altaId: string, clave = claveIdempotencia()) =>
            api.pedir<Alta>('POST', r(businessId, `/channel-connections/${id(altaId)}/cancel`), { idempotencia: clave }),

        // ---- 11.4 Plantillas de Meta ----
        /** listMetaTemplates (`canales.plantillas.gestionar`, o `canales.buzon.atender` solo para ver: G686). */
        listMetaTemplates: (businessId: string, filtros: ConsultaPlantillas & Pagina = {}) =>
            api.pedir<ListaPlantillasMeta>('GET', r(businessId, `/meta-templates${consulta({ ...filtros })}`)),
        /** getMetaTemplate: contenido, estado con su motivo e historial. */
        getMetaTemplate: (businessId: string, plantillaId: string) => api.pedir<PlantillaMeta>('GET', r(businessId, `/meta-templates/${id(plantillaId)}`)),
        /** createMetaTemplate: desde el panel, solo de marketing y con `requested_by = panel` (G448). */
        createMetaTemplate: (businessId: string, datos: NuevaPlantillaMeta, clave = claveIdempotencia()) =>
            api.pedir<PlantillaMeta>('POST', r(businessId, '/meta-templates'), { cuerpo: datos, idempotencia: clave }),
        /** withdrawMetaTemplate: la borra de la cuenta en Meta (también en solo lectura). */
        withdrawMetaTemplate: (businessId: string, plantillaId: string, clave = claveIdempotencia()) =>
            api.pedir<PlantillaMeta>('POST', r(businessId, `/meta-templates/${id(plantillaId)}/withdraw`), { idempotencia: clave }),
    };
}
